import React, { useCallback, useEffect, useRef, useState } from 'react'
import { TransformComponent, TransformWrapper } from 'react-zoom-pan-pinch'
import { HiX, HiChevronLeft, HiChevronRight, HiOutlineZoomIn, HiOutlineMinus, HiOutlinePlus, HiOutlineRefresh, HiPlay, HiPause, HiVolumeUp, HiVolumeOff } from 'react-icons/hi'
import { cleanCopy } from '../util/imageFile'

const MAX_SCALE = 4
const canHoverZoom = () => typeof window !== 'undefined' && window.matchMedia
    && window.matchMedia('(min-width: 1024px) and (hover: hover) and (pointer: fine) and (prefers-reduced-motion: no-preference)').matches
const PHONE_QUERY = '(max-width: 767px)'
const reducedMotion = () => typeof window !== 'undefined' && !!window.matchMedia
    && window.matchMedia('(prefers-reduced-motion: reduce)').matches

// true on phone-sized screens; product videos are shown there only
const useIsPhone = () => {
    const [phone, setPhone] = useState(() => typeof window !== 'undefined' && !!window.matchMedia && window.matchMedia(PHONE_QUERY).matches)
    useEffect(() => {
        if (!window.matchMedia) return undefined
        const query = window.matchMedia(PHONE_QUERY)
        const onChange = () => setPhone(query.matches)
        if (query.addEventListener) query.addEventListener('change', onChange)
        else query.addListener(onChange)
        return () => {
            if (query.removeEventListener) query.removeEventListener('change', onChange)
            else query.removeListener(onChange)
        }
    }, [])
    return phone
}

// Product photos: a 3:4 main photo (tap/click opens a full-screen zoom), 3:4 thumbnails, and on desktop a 2x
// magnifier beside the photo while hovering. `images` = [{ full, thumb }]; `current` = the shown full URL.
// `video` = { url, poster, first } (optional): on phones only, a muted looping slide (first, or after the main photo)
// that opens a full-screen Reel player with sound.
const ProductGallery = ({ images, current, title, onSelect, video }) => {
    const [zoomAt, setZoomAt] = useState(null) // index shown in the full-screen viewer
    const [lens, setLens] = useState(null) // { x, y } in % while hovering on desktop
    const isPhone = useIsPhone()
    const hasVideo = !!(video && video.url) && isPhone
    const [videoShown, setVideoShown] = useState(() => !!(video && video.first))
    const [reelOpen, setReelOpen] = useState(false)
    const mainRef = useRef(null)
    const index = Math.max(0, images.findIndex(img => img.full === current))
    const shown = images[index] || images[0]
    const showingVideo = hasVideo && videoShown

    const selectPhoto = (full) => {
        setVideoShown(false)
        onSelect(full)
    }

    const onMove = (e) => {
        if (!canHoverZoom() || !mainRef.current) return
        const box = mainRef.current.getBoundingClientRect()
        const x = Math.min(100, Math.max(0, ((e.clientX - box.left) / box.width) * 100))
        const y = Math.min(100, Math.max(0, ((e.clientY - box.top) / box.height) * 100))
        setLens({ x, y })
    }

    if (!shown) return null

    const photoThumbs = images.map((img, i) =>
        <button type='button' key={img.full + i}
            className={!showingVideo && i === index ? 'mv-pdp-thumb current' : 'mv-pdp-thumb'}
            onClick={() => selectPhoto(img.full)}
            aria-label={`Show image ${i + 1}`} aria-pressed={!showingVideo && i === index}>
            <img src={cleanCopy(img.thumb, img.full)} alt='' loading='lazy' />
        </button>
    )
    const videoThumb = hasVideo &&
        <button type='button' key='video' className={showingVideo ? 'mv-pdp-thumb mv-pdp-thumb-video current' : 'mv-pdp-thumb mv-pdp-thumb-video'}
            onClick={() => setVideoShown(true)} aria-label='Show video' aria-pressed={showingVideo}>
            {video.poster ? <img src={video.poster} alt='' loading='lazy' /> : <span className='mv-pdp-thumb-video-blank' />}
            <span className='mv-pdp-thumb-play' aria-hidden='true'><HiPlay /></span>
        </button>
    const thumbs = !hasVideo ? photoThumbs
        : video.first ? [videoThumb, ...photoThumbs]
            : [photoThumbs[0], videoThumb, ...photoThumbs.slice(1)]

    return (
        <section className='mv-pdp-gallery' aria-label='Product images'>
            {showingVideo ?
                <VideoSlide video={video} title={title} paused={reelOpen} onOpen={() => setReelOpen(true)} /> :
            <div className='mv-pdp-main-wrap'>
                <button type='button' className='mv-pdp-main' ref={mainRef} onClick={() => { setLens(null); setZoomAt(index) }}
                    onMouseMove={onMove} onMouseLeave={() => setLens(null)} aria-label={`Zoom photo of ${title}`}>
                    <img src={shown.full} alt={title} />
                    <span className='mv-pdp-zoom-hint' aria-hidden='true'><HiOutlineZoomIn /> Tap to zoom</span>
                    {lens && <span className='mv-pdp-lens-spot' style={{ left: lens.x + '%', top: lens.y + '%' }} aria-hidden='true' />}
                </button>
                {lens &&
                    <div className='mv-pdp-lens' aria-hidden='true'
                        style={{ backgroundImage: `url("${shown.full}")`, backgroundPosition: `${lens.x}% ${lens.y}%` }} />}
            </div>}

            {thumbs.length > 1 && <div className='mv-pdp-thumbs'>{thumbs}</div>}

            {reelOpen && <ReelViewer video={video} title={title} onClose={() => setReelOpen(false)} />}

            {zoomAt != null &&
                <ZoomViewer images={images} start={zoomAt} title={title}
                    onClose={(at) => { setZoomAt(null); if (images[at]) onSelect(images[at].full) }} />}
        </section>
    )
}

// The video in the 3:4 gallery frame: muted, looping, playing only while on screen (no autoplay with reduced
// motion). Tapping opens the Reel player with sound.
const VideoSlide = ({ video, title, paused, onOpen }) => {
    const ref = useRef(null)
    const [failed, setFailed] = useState(false)
    const autoplay = !reducedMotion()

    useEffect(() => {
        const el = ref.current
        if (!el || failed) return undefined
        if (paused || !autoplay || typeof IntersectionObserver === 'undefined') {
            el.pause()
            return undefined
        }
        const observer = new IntersectionObserver(([entry]) => {
            if (entry.isIntersecting && entry.intersectionRatio >= 0.5) {
                const playing = el.play()
                if (playing && playing.catch) playing.catch(() => { /* autoplay refused: the poster stays */ })
            } else {
                el.pause()
            }
        }, { threshold: [0, 0.5] })
        observer.observe(el)
        return () => { observer.disconnect(); el.pause() }
    }, [paused, autoplay, failed])

    return (
        <div className='mv-pdp-main-wrap'>
            <button type='button' className='mv-pdp-main mv-pdp-video' onClick={onOpen} aria-label={`Play video of ${title} with sound`}>
                {failed ?
                    <img src={video.poster} alt={title} /> :
                    <video ref={ref} src={video.url} poster={video.poster} muted loop playsInline preload='metadata'
                        onError={() => setFailed(true)} />}
                <span className='mv-pdp-zoom-hint mv-pdp-video-hint' aria-hidden='true'>
                    {autoplay ? <><HiVolumeUp /> Tap for sound</> : <><HiPlay /> Play video</>}
                </span>
            </button>
        </div>
    )
}

// Full-screen 9:16 player with sound, like a Reel: tap pauses or plays, a mute button and a thin progress bar;
// Esc or X closes.
const ReelViewer = ({ video, title, onClose }) => {
    const ref = useRef(null)
    const dialogRef = useRef(null)
    const closeRef = useRef(onClose)
    closeRef.current = onClose
    const [playing, setPlaying] = useState(true)
    const [muted, setMuted] = useState(false)
    const [progress, setProgress] = useState(0)

    useEffect(() => {
        const root = document.documentElement
        const lockedBefore = root.classList.contains('mv-scroll-locked')
        if (!lockedBefore) root.classList.add('mv-scroll-locked')
        const returnFocus = document.activeElement
        const onKey = (e) => { if (e.key === 'Escape') closeRef.current() }
        document.addEventListener('keydown', onKey)
        if (dialogRef.current) dialogRef.current.focus({ preventScroll: true })
        const el = ref.current
        if (el) {
            const started = el.play()
            // a browser that refuses sound here still plays it muted
            if (started && started.catch) started.catch(() => { el.muted = true; setMuted(true); el.play().catch(() => setPlaying(false)) })
        }
        return () => {
            if (!lockedBefore) root.classList.remove('mv-scroll-locked')
            document.removeEventListener('keydown', onKey)
            if (returnFocus && returnFocus.focus && document.contains(returnFocus)) returnFocus.focus({ preventScroll: true })
        }
    }, [])

    const togglePlay = () => {
        const el = ref.current
        if (!el) return
        if (el.paused) el.play().catch(() => { })
        else el.pause()
    }

    const toggleMute = (e) => {
        e.stopPropagation()
        const el = ref.current
        if (!el) return
        el.muted = !el.muted
        setMuted(el.muted)
    }

    return (
        <div className='mv-reel' role='dialog' aria-modal='true' aria-label={`${title}: video`} tabIndex={-1} ref={dialogRef}>
            <div className='mv-reel-stage' onClick={togglePlay}>
                <video ref={ref} className='mv-reel-video' src={video.url} poster={video.poster} loop playsInline
                    onPlay={() => setPlaying(true)} onPause={() => setPlaying(false)}
                    onTimeUpdate={(e) => { const v = e.currentTarget; if (v.duration) setProgress(v.currentTime / v.duration) }} />
                {!playing && <span className='mv-reel-paused' aria-hidden='true'><HiPlay /></span>}
            </div>
            <div className='mv-reel-progress' aria-hidden='true'><span style={{ width: (progress * 100) + '%' }} /></div>
            <div className='mv-reel-top'>
                <span className='mv-reel-title'>{title}</span>
                <button type='button' className='mv-zoom-btn' aria-label='Close video' onClick={() => onClose()}><HiX /></button>
            </div>
            <div className='mv-reel-tools'>
                <button type='button' className='mv-zoom-btn' aria-label={playing ? 'Pause' : 'Play'} onClick={(e) => { e.stopPropagation(); togglePlay() }}>
                    {playing ? <HiPause /> : <HiPlay />}
                </button>
                <button type='button' className='mv-zoom-btn' aria-label={muted ? 'Turn sound on' : 'Mute'} onClick={toggleMute}>
                    {muted ? <HiVolumeOff /> : <HiVolumeUp />}
                </button>
            </div>
        </div>
    )
}

// Full-screen viewer: pinch / double-tap / wheel / buttons zoom up to 4x and drag to pan; swipe, arrow keys or the
// side buttons change photo; Esc or X closes. The whole photo is shown (never cropped).
const ZoomViewer = ({ images, start, title, onClose }) => {
    const [at, setAt] = useState(start)
    const [scale, setScale] = useState(1)
    const dialogRef = useRef(null)
    const swipe = useRef(null)
    const atRef = useRef(at)
    atRef.current = at
    const closeRef = useRef(onClose)
    closeRef.current = onClose
    const many = images.length > 1
    const zoomRef = useRef(null)

    // double-click / double-tap zooms in (library); when already zoomed in, it goes back to the whole photo
    const zoomBackOut = () => { if (scale > 1.01 && zoomRef.current) zoomRef.current.resetTransform() }

    const go = useCallback((step) => {
        setAt(i => (i + step + images.length) % images.length)
        setScale(1)
    }, [images.length])

    useEffect(() => {
        const root = document.documentElement
        const lockedBefore = root.classList.contains('mv-scroll-locked')
        if (!lockedBefore) root.classList.add('mv-scroll-locked')
        const returnFocus = document.activeElement
        const onKey = (e) => {
            if (e.key === 'Escape') closeRef.current(atRef.current)
            else if (e.key === 'ArrowRight' && many) go(1)
            else if (e.key === 'ArrowLeft' && many) go(-1)
        }
        document.addEventListener('keydown', onKey)
        if (dialogRef.current) dialogRef.current.focus({ preventScroll: true })
        return () => {
            if (!lockedBefore) root.classList.remove('mv-scroll-locked')
            document.removeEventListener('keydown', onKey)
            if (returnFocus && returnFocus.focus && document.contains(returnFocus)) returnFocus.focus({ preventScroll: true })
        }
    }, [go, many])

    // a one-finger horizontal swipe changes photo when not zoomed in
    const onTouchStart = (e) => {
        swipe.current = e.touches.length === 1 && scale <= 1.01 ? { x: e.touches[0].clientX, y: e.touches[0].clientY } : null
    }
    const onTouchEnd = (e) => {
        if (!swipe.current || !many) return
        const dx = e.changedTouches[0].clientX - swipe.current.x
        const dy = e.changedTouches[0].clientY - swipe.current.y
        swipe.current = null
        if (Math.abs(dx) > 50 && Math.abs(dx) > Math.abs(dy) * 1.5) go(dx < 0 ? 1 : -1)
    }

    const image = images[at]

    return (
        <div className='mv-zoom' role='dialog' aria-modal='true' aria-label={`${title}: photo ${at + 1} of ${images.length}`}
            tabIndex={-1} ref={dialogRef}>
            <div className='mv-zoom-top'>
                <span className='mv-zoom-count'>{many ? `${at + 1} / ${images.length}` : ''}</span>
                <button type='button' className='mv-zoom-btn' aria-label='Close' onClick={() => onClose(at)}><HiX /></button>
            </div>

            <div className='mv-zoom-stage' onTouchStart={onTouchStart} onTouchEnd={onTouchEnd} onDoubleClick={zoomBackOut}>
                <TransformWrapper key={at} ref={zoomRef} minScale={1} maxScale={MAX_SCALE} centerOnInit
                    doubleClick={{ disabled: scale > 1.01, mode: 'zoomIn', step: 1.2 }} wheel={{ step: 0.15 }} pinch={{ step: 5 }}
                    panning={{ disabled: scale <= 1.01, velocityDisabled: true }}
                    onTransformed={(_, state) => setScale(state.scale)}>
                    {({ zoomIn, zoomOut, resetTransform }) => <>
                        <TransformComponent wrapperClass='mv-zoom-wrap' contentClass='mv-zoom-content'>
                            <img className='mv-zoom-img' src={image.full} alt={`${title} (${at + 1} of ${images.length})`} draggable={false} />
                        </TransformComponent>
                        <div className='mv-zoom-tools'>
                            <button type='button' className='mv-zoom-btn' aria-label='Zoom out' onClick={() => zoomOut(0.5)} disabled={scale <= 1.01}>
                                <HiOutlineMinus />
                            </button>
                            <span className='mv-zoom-scale' aria-live='polite'>{Math.round(scale * 100)}%</span>
                            <button type='button' className='mv-zoom-btn' aria-label='Zoom in' onClick={() => zoomIn(0.5)} disabled={scale >= MAX_SCALE - 0.01}>
                                <HiOutlinePlus />
                            </button>
                            <button type='button' className='mv-zoom-btn' aria-label='Reset zoom' onClick={() => resetTransform()} disabled={scale <= 1.01}>
                                <HiOutlineRefresh />
                            </button>
                        </div>
                    </>}
                </TransformWrapper>

                {many && <>
                    <button type='button' className='mv-zoom-btn mv-zoom-prev' aria-label='Previous photo' onClick={() => go(-1)}><HiChevronLeft /></button>
                    <button type='button' className='mv-zoom-btn mv-zoom-next' aria-label='Next photo' onClick={() => go(1)}><HiChevronRight /></button>
                </>}
            </div>

            <p className='mv-zoom-help'>Pinch or double-tap to zoom{many ? ' · swipe for more photos' : ''}</p>

            {many &&
                <div className='mv-zoom-thumbs'>
                    {images.map((img, i) =>
                        <button type='button' key={img.full + i} className={i === at ? 'mv-zoom-thumb current' : 'mv-zoom-thumb'}
                            onClick={() => { setAt(i); setScale(1) }} aria-label={`Photo ${i + 1}`} aria-pressed={i === at}>
                            <img src={cleanCopy(img.thumb, img.full)} alt='' />
                        </button>)}
                </div>}
        </div>
    )
}

export default ProductGallery
